import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  useNavigationType,
  useSearchParams,
} from "react-router";
import type { Site } from "@/lib/api/client";
import { useSites } from "@/lib/hooks/use-sites";

/**
 * The site scope every section reads (ADR 013, principle 2).
 *
 * One control in the header - all sites, or one - kept in the URL as
 * `?site=<id>` so a link carries its scope, and the only reader and writer of
 * that parameter. It replaced four per-page site filters, each of which read
 * and wrote the URL on its own.
 *
 * The Explorer keeps its multi-site `siteId` list as a refinement while the
 * scope is "all sites"; nothing else does.
 */

export const SITE_SCOPE_PARAM = "site";

/**
 * Parameters that name something belonging to the site in scope. Changing
 * site invalidates them, so they are dropped with it rather than left to
 * open a drawer on another site's row.
 */
const SITE_SCOPED_PARAMS = ["signature", "script"] as const;

/**
 * Navigation state marking a scope the reader chose in the header, as
 * opposed to one a link carried or dropped. `useSiteScopeContinuity` leaves
 * a chosen scope alone.
 */
const SCOPE_CHOSEN_STATE = "siteScopeChosen";

function isScopeChosen(state: unknown): boolean {
  return (
    typeof state === "object" &&
    state !== null &&
    (state as Record<string, unknown>)[SCOPE_CHOSEN_STATE] === true
  );
}

export interface SiteScope {
  /** The site in scope, or undefined for all of them. */
  siteId: string | undefined;
  site: Site | undefined;
  /**
   * Whether the reader chose this site, rather than it being the only one
   * they have. A filter that can hide rows belonging to no site should
   * follow a choice, not an accident of having one site.
   */
  isExplicit: boolean;
  sites: Site[];
  isLoading: boolean;
  setSiteId: (siteId: string | undefined) => void;
}

export function useSiteScope(): SiteScope {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading } = useSites();
  const sites = useMemo(() => data?.sites ?? [], [data?.sites]);

  // A shared link can name a site this tenant no longer has, or never had:
  // that reads as all sites rather than as an empty view of nothing.
  //
  // A tenant with one site is always scoped to it: "all sites" and "that
  // site" show the same thing, and the sections that need a site - the
  // reliability tabs, the scripts views - would otherwise have none.
  const { site, isExplicit } = useMemo(() => {
    const id = searchParams.get(SITE_SCOPE_PARAM);
    const named = id
      ? sites.find((candidate) => candidate.id === id)
      : undefined;
    return {
      site: named ?? (sites.length === 1 ? sites[0] : undefined),
      isExplicit: named !== undefined,
    };
  }, [searchParams, sites]);

  const setSiteId = useCallback(
    (siteId: string | undefined) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (siteId) params.set(SITE_SCOPE_PARAM, siteId);
          else params.delete(SITE_SCOPE_PARAM);
          for (const key of SITE_SCOPED_PARAMS) params.delete(key);
          return params;
        },
        { state: { [SCOPE_CHOSEN_STATE]: true } },
      );
    },
    [setSearchParams],
  );

  return { siteId: site?.id, site, isExplicit, sites, isLoading, setSiteId };
}

/**
 * The scope as a section link should carry it, so that moving from Policy to
 * Headers keeps the site the reader chose. Sidebar and tab links are built
 * with this rather than with a bare href.
 */
export function withSiteScope(
  href: string,
  siteId: string | undefined,
): string {
  return siteId
    ? `${href}?${SITE_SCOPE_PARAM}=${encodeURIComponent(siteId)}`
    : href;
}

export interface SiteScopeContinuity {
  /**
   * The site a link narrowed "all sites" to, until the reader chooses a
   * scope or the site changes again; undefined otherwise.
   */
  narrowedTo: string | undefined;
  dismiss: () => void;
}

/**
 * Keeps the reader's site scope across the dashboard, and says when a link
 * changes it (task 453). Mounted once, by the dashboard layout.
 *
 * - A link that names no site keeps the one in scope. Most links are built
 *   with `withSiteScope`, but not every one is (the user menu's Settings
 *   link dropped it, and the reader came back to "All sites"); fixing the
 *   URL here covers the links nobody remembered.
 * - A link that names a site while the scope is "all sites" narrows it, as
 *   an Overview row does to its own site. That stays, because the row's
 *   page is about that site, but `narrowedTo` names it so the layout can
 *   say so and offer the way back.
 *
 * Back and forward are history, not links, and are left as they were; so is
 * anything the header's control chose.
 */
export function useSiteScopeContinuity(): SiteScopeContinuity {
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigate = useNavigate();
  const { data } = useSites();
  const previousSite = useRef<string | null | undefined>(undefined);
  const [narrowedTo, setNarrowedTo] = useState<string | undefined>();

  // A site id that names none of the tenant's sites, such as a deleted one
  // in an old link, is "all sites" to `useSiteScope`, so it is not a scope to
  // carry forward. Until the sites load, an id is taken at its word.
  const siteIds = useRef<Set<string> | undefined>(undefined);
  siteIds.current = data
    ? new Set(data.sites.map((site) => site.id))
    : undefined;

  // Before paint, so the page does not draw "all sites" first.
  useLayoutEffect(() => {
    const params = new URLSearchParams(location.search);
    const known = (id: string | null | undefined) =>
      id && (!siteIds.current || siteIds.current.has(id)) ? id : null;
    const named = params.get(SITE_SCOPE_PARAM);
    const site = known(named);
    const previous = previousSite.current;
    // Resolved now, not when it was stored: the first location is read
    // before the sites have loaded.
    const before = previous === undefined ? undefined : known(previous);
    previousSite.current = site;

    // The first location, history, and the header's own choice.
    if (
      before === undefined ||
      navigationType === "POP" ||
      isScopeChosen(location.state)
    ) {
      setNarrowedTo(undefined);
      return;
    }

    // Only a link that names no site at all: one naming a site, even one
    // that is gone, said what it meant.
    if (before && !named) {
      params.set(SITE_SCOPE_PARAM, before);
      previousSite.current = before;
      navigate(
        {
          pathname: location.pathname,
          search: `?${params}`,
          hash: location.hash,
        },
        { replace: true, state: location.state },
      );
      return;
    }

    if (!before && site) setNarrowedTo(site);
    else if (site !== before) setNarrowedTo(undefined);
  }, [location, navigationType, navigate]);

  const dismiss = useCallback(() => setNarrowedTo(undefined), []);
  return { narrowedTo, dismiss };
}
